<template>
  <div class="hm-reslist">
    <div class="hm-reslist__heading">
      <h1 class="hm-reslist__title">Reservation List</h1>
      <HMButton
        v-if="is_general_manager"
        @click.native="onShowCountModal"
        background="linear-gradient(180deg, #DAEDFD 0%, #D2E7FB 100%)" color="#499DAE"
      >
        <PrintIcon/>
        <span>Guest Count</span>
      </HMButton>
    </div>
    <nav class="hm-reslist__tabs" aria-label="Reservation sections">
      <router-link
        exact
        class="hm-reslist__tabs-item"
        active-class="hm-reslist__tabs-item--active"
        :to="{ name: 'hotel-management-reservations-list-upcoming' }"
      >
        <UpcomingTabIcon class="hm-reslist__tabs-icon"/>
        <span class="hm-reslist__tabs-text">Upcoming</span>
      </router-link>
      <router-link
        class="hm-reslist__tabs-item"
        active-class="hm-reslist__tabs-item--active"
        :to="{ name: 'hotel-management-reservations-list-inhouse' }"
      >
        <InHouseTabIcon class="hm-reslist__tabs-icon"/>
        <span class="hm-reslist__tabs-text">In House</span>
      </router-link>
      <router-link
        class="hm-reslist__tabs-item"
        active-class="hm-reslist__tabs-item--active"
        :to="{ name: 'hotel-management-reservations-list-archive' }"
      >
        <ArchiveTabIcon class="hm-reslist__tabs-icon"/>
        <span class="hm-reslist__tabs-text">Archive</span>
      </router-link>
      <router-link
        class="hm-reslist__tabs-item"
        active-class="hm-reslist__tabs-item--active"
        :to="{ name: 'hotel-management-reservations-list-cancelled' }"
      >
        <CancelledTabIcon class="hm-reslist__tabs-icon"/>
        <span class="hm-reslist__tabs-text">Cancelled</span>
      </router-link>
    </nav>
    <transition name="fade" mode="out-in">
      <router-view />
    </transition>
    <modal
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      :width="400"
      name="guest_count"
      class="modal-not-overflow"
    >
      <div class="popup-new">
        <button class="popup-new__close" type="button" @click="$modal.hide('guest_count')"></button>
        <div class="popup-new__title">Guest Count</div>
        <div class="popup-new__body">
          <form @submit.prevent="onCheckForm">
            <app-form-field label="Property" class="family" required :marginBottom="20">
              <app-select
                v-model="form.property"
                :options="property_options"
                :clearable="false"
                placeholder="Property"
                class="family"
                :error="$v.form.property.$error"
              />
              <template #error>
                <div v-if="$v.form.property.$dirty && !$v.form.property.required">Required field</div>
              </template>
            </app-form-field>
            <app-form-field label="Period" class="family" required :marginBottom="20">
              <div class="date-picker-wrapper">
                <VueCtkDateTimePicker
                  id="date_picker_balance"
                  class="custom-date-time-picker custom-date-time-picker--range family"
                  v-model="form.dates"
                  :custom-shortcuts="custom_shortcuts"
                  shortcut="this_month"
                  format="YYYY-MM-DD"
                  formatted="ll"
                  label="Select dates"
                  color="#77b017"
                  button-color="#77b017"
                  overlay
                  no-header
                  no-label
                  locale="en"
                  range
                  noClearButton
                  :class="{'date-time-picker--error': $v.form.dates.start.$error || $v.form.dates.end.$error}"
                />
              </div>
              <template #error>
                <div v-if="$v.form.dates.$dirty && (!$v.form.dates.start.required || !$v.form.dates.end.required)">Required field</div>
              </template>
            </app-form-field>
            <app-cells :indent="false" position="center">
              <HMButton :loading="isSubmittingForm"><span>Get</span></HMButton>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import InHouseTabIcon from '@/views/hotel-management/reservations/detail/icons/InHouseTabIcon.vue'
import ArchiveTabIcon from '@/views/hotel-management/reservations/detail/icons/ArchiveTabIcon.vue'
import CancelledTabIcon from '@/views/hotel-management/reservations/detail/icons/CancelledTabIcon.vue'
import UpcomingTabIcon from '@/views/hotel-management/reservations/detail/icons/UpcomingTabIcon.vue'
import PrintIcon from '@/views/hotel-management/reservations/detail/icons/PrintIcon.vue'
import HMButton from '@/views/hotel-management/reservations/components/HMButton.vue'
import {
  getHotelManagementPropertiesV2,
  getHotelManagementExportGuestCount
} from '@/http'
import { required } from 'vuelidate/lib/validators'
import { format, parseISO } from 'date-fns'
import moment from 'moment/moment'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'

export default {
  name: 'HotelManagementReservationsListLayout',
  components: {HMButton, PrintIcon, ArchiveTabIcon, CancelledTabIcon, InHouseTabIcon, UpcomingTabIcon, VueCtkDateTimePicker},
  data() {
    return {
      form: {
        dates: {},
      },
      isSubmittingForm: false,
      property_options: [],
      custom_shortcuts: [
        {
          key: 'this_month',
          label: 'This month',
          value: () => {
            return {
              start: moment().startOf('month'),
              end: moment().endOf('month')
            }
          }
        },
        {
          key: 'next_month',
          label: 'Next month',
          value: () => {
            return {
              start: moment().startOf('month').add(1, 'months'),
              end: moment().endOf('month').add(1, 'months')
            }
          }
        },
      ],
    }
  },
  validations: {
    form: {
      property: {required},
      dates: {
        start: {required},
        end: {required},
      },
    },
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  created() {
    getHotelManagementPropertiesV2().then(response => { this.property_options = response.data })
  },
  methods: {
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.isSubmittingForm = true
      getHotelManagementExportGuestCount({property: this.form.property.code, from_date: this.form.dates.start, to_date: this.form.dates.end})
        .finally(() => { this.isSubmittingForm = false })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `Guest Count ${this.form.property.name} (${this.dateFormat(this.form.dates.start)} - ${this.dateFormat(this.form.dates.end)}).xlsx`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
          this.$modal.hide('guest_count')
        })
    },
    onShowCountModal() {
      this.form = {
        dates: {}
      }
      this.$v.form.$reset()
      this.$modal.show('guest_count')
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
  }
}
</script>

<style>
.hm-reslist__tabs {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 50px;
}

.hm-reslist__tabs-item {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  border-radius: 17px;
  border: 1px solid #ECEAE5;
  background-color: #FCFCFC;
  box-shadow: 0 1px 5px 0 rgba(159, 167, 177, 0.20);
  color: #8A8A8A;
  text-decoration: none;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 15px;
  line-height: 18px;
  font-weight: 500;
  transition: color 0.3s;
}

.hm-reslist__tabs-item:hover {
  color: #000
}

.hm-reslist__tabs-item--active {
  border: 1px solid #A3A3A3;
  color: #000;

  .hm-reslist__tabs-icon {
    filter: none;
  }
}

.hm-reslist__tabs-icon {
  filter: grayscale(100%);
}

.hm-reslist__tabs-text {
  white-space: nowrap;
}

.hm-reslist__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 40px;
}

.hm-reslist__title {
  color: #000;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 20px;
  font-weight: 600;
}

.hm-reslist__subtitle {
  margin-bottom: 30px;
  color: #000;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 18px;
  font-weight: 600;
}
</style>